<script setup>
import { computed, onMounted, onUnmounted, ref, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import CustomToolsPageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/CustomToolsPageEmptyState.vue';
import CreateCustomToolDialog from 'dashboard/components-next/captain/pageComponents/customTool/CreateCustomToolDialog.vue';
import CustomToolCard from 'dashboard/components-next/captain/pageComponents/customTool/CustomToolCard.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';

const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('captainCustomTools/getUIFlags');
const customTools = useMapGetter('captainCustomTools/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);
const customToolsMeta = useMapGetter('captainCustomTools/getMeta');

// An indexing run takes minutes and finishes in a background job, so the page has to ask.
// Only the tools that are actually running are refetched, one record at a time, which
// leaves the list untouched instead of blanking it into its loading state every few
// seconds the way refetching the whole page would.
const INDEXING_POLL_INTERVAL = 5000;

const createDialogRef = ref(null);
const deleteDialogRef = ref(null);
const selectedTool = ref(null);
const dialogType = ref('');

const fetchCustomTools = (page = 1) => {
  store.dispatch('captainCustomTools/get', { page });
};

const indexingToolIds = computed(() =>
  customTools.value
    .filter(tool => ['queued', 'running'].includes(tool.indexing?.status))
    .map(tool => tool.id)
);

const pollIndexingTools = () => {
  indexingToolIds.value.forEach(id => {
    // A failed refresh is the next tick's problem: stopping the poll over one would leave
    // the card frozen on whatever it last said.
    store.dispatch('captainCustomTools/show', id).catch(() => {});
  });
};

let pollTimer = null;

const stopPolling = () => {
  if (!pollTimer) return;

  clearInterval(pollTimer);
  pollTimer = null;
};

watch(
  () => indexingToolIds.value.length,
  count => {
    if (count && !pollTimer) {
      pollTimer = setInterval(pollIndexingTools, INDEXING_POLL_INTERVAL);
    } else if (!count) {
      stopPolling();
    }
  },
  // Immediate because the list can already be in the store when the page is opened a
  // second time, and then the count never changes for the watcher to notice.
  { immediate: true }
);

const onPageChange = page => fetchCustomTools(page);

const openCreateDialog = () => {
  dialogType.value = 'create';
  selectedTool.value = null;
  nextTick(() => createDialogRef.value.dialogRef.open());
};

const handleEdit = tool => {
  dialogType.value = 'edit';
  selectedTool.value = tool;
  nextTick(() => createDialogRef.value.dialogRef.open());
};

const handleDelete = tool => {
  selectedTool.value = tool;
  nextTick(() => deleteDialogRef.value.dialogRef.open());
};

const handleReindex = async tool => {
  if (!tool) {
    return;
  }

  try {
    await store.dispatch('captainCustomTools/reindex', tool.id);
    // Reread it so the card picks up the queued mark, which is what starts the polling.
    await store.dispatch('captainCustomTools/show', tool.id);
    useAlert(t('CAPTAIN.CUSTOM_TOOLS.REINDEX.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('CAPTAIN.CUSTOM_TOOLS.REINDEX.ERROR_MESSAGE'));
  }
};

const handleAction = async ({ action, id }) => {
  const tool = customTools.value.find(toolItem => toolItem.id === id);
  if (!tool) {
    return;
  }

  if (action === 'edit') {
    handleEdit(tool);
  } else if (action === 'delete') {
    handleDelete(tool);
  } else if (action === 'reindex') {
    await handleReindex(tool);
  }
};

const handleDialogClose = () => {
  dialogType.value = '';
  selectedTool.value = null;
};

const onDeleteSuccess = () => {
  selectedTool.value = null;
  // Check if page will be empty after deletion
  if (customTools.value.length === 1 && customToolsMeta.value.page > 1) {
    // Go to previous page if current page will be empty
    onPageChange(customToolsMeta.value.page - 1);
  } else {
    // Refresh current page
    fetchCustomTools(customToolsMeta.value.page);
  }
};

onMounted(() => {
  fetchCustomTools();
});

onUnmounted(stopPolling);
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.CUSTOM_TOOLS.HEADER')"
    :button-label="$t('CAPTAIN.CUSTOM_TOOLS.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="customToolsMeta.totalCount"
    :current-page="customToolsMeta.page"
    :show-pagination-footer="!isFetching && !!customTools.length"
    :is-fetching="isFetching"
    :is-empty="!customTools.length"
    :feature-flag="FEATURE_FLAGS.CAPTAIN_V2"
    :show-know-more="false"
    @update:current-page="onPageChange"
    @click="openCreateDialog"
  >
    <template #paywall>
      <CaptainPaywall />
    </template>

    <template #emptyState>
      <CustomToolsPageEmptyState @click="openCreateDialog" />
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <CustomToolCard
          v-for="tool in customTools"
          :id="tool.id"
          :key="tool.id"
          :title="tool.title"
          :description="tool.description"
          :endpoint-url="tool.endpoint_url"
          :http-method="tool.http_method"
          :auth-type="tool.auth_type"
          :template-id="tool.template_id"
          :param-schema="tool.param_schema"
          :enabled="tool.enabled"
          :created-at="tool.created_at"
          :updated-at="tool.updated_at"
          :indexing="tool.indexing"
          @action="handleAction"
        />
      </div>
    </template>
  </PageLayout>

  <CreateCustomToolDialog
    v-if="dialogType"
    ref="createDialogRef"
    :type="dialogType"
    :selected-tool="selectedTool"
    @close="handleDialogClose"
  />

  <DeleteDialog
    v-if="selectedTool"
    ref="deleteDialogRef"
    :entity="selectedTool"
    type="CustomTools"
    translation-key="CUSTOM_TOOLS"
    @delete-success="onDeleteSuccess"
  />
</template>
